import { WorkItemState, WorkItemSummary } from '@robocorp/robocloud-search-iternal-sdk';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import * as searchService from '~/services/searchAPI';

import { useLazyQuery } from '../useLazyQuery';
import {
  ApiQueryFnData,
  formatErrorPayload,
  UseApiQueryOptions,
  useCurrentWorkspaceQueryKey,
  validateApiResponse,
} from '../utils';
import { WithWorkspaceId } from '~/types/common';

export const defaultWorkItemSummary = {
  [WorkItemState.New]: 0,
  [WorkItemState.Registered]: 0,
  [WorkItemState.Pending]: 0,
  [WorkItemState.InProgress]: 0,
  [WorkItemState.Failed]: 0,
  [WorkItemState.Completed]: 0,
};

export const getWorkItemSummaryQueryKey = (args: { processId: string }) => [
  'workItemSummary',
  args,
];

export enum WorkItemSummaryErrors {
  GET_WORK_ITEM_SUMMARY_ERROR = 'GET_WORK_ITEM_SUMMARY_ERROR',
}

export const fetchWorkItemSummary = (args: { processId: string } & WithWorkspaceId) => async () => {
  const response = validateApiResponse(
    await searchService.getWorkItemSummary({
      workspaceId: args.workspaceId,
      processId: args.processId,
    }),
  );
  return response.data?.data;
};

export const useWorkItemSummaryQuery = <T extends typeof searchService.getWorkItemSummary>(
  processId: string,
  options?: UseApiQueryOptions<T, ApiQueryFnData<T>['data']>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getWorkItemSummaryQueryKey({ processId }),
    fetchWorkItemSummary({ workspaceId, processId }),
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemSummaryErrors.GET_WORK_ITEM_SUMMARY_ERROR,
          }),
        );
      },
      ...options,
    },
  );
};

export const useLazyWorkItemSummaryQuery = () => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useLazyQuery<{ processId: string }, WorkItemSummary | undefined, Error>(
    (args) => getWorkItemSummaryQueryKey(args),
    (args) => fetchWorkItemSummary({ workspaceId, ...args }),
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkItemSummaryErrors.GET_WORK_ITEM_SUMMARY_ERROR,
          }),
        );
      },
      refetchOnWindowFocus: true,
    },
  );
};
